/**
 * FreeOnboardingTryOnModal — Cadeau de bienvenue : 1 Try-On gratuit
 * pour les nouveaux users Free, avec animation et explication.
 *
 * v1.12.603 — V5.5 bundle v5.
 * Thème Mytenue : noir #1a1f36 / doré #D4AF37 / cream #F4ECDD.
 */
import React, { useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Animated, Easing } from 'react-native';
import AppModal from './AppModal';
import { useI18n } from '../i18n';

const COLORS = {
  cream:    '#F4ECDD',
  gold:     '#D4AF37',
  goldDark: '#B8860B',
  navy:     '#1a1f36',
  text:     '#1A1A1A',
  textLight:'#666',
  white:    '#FFF',
};

export default function FreeOnboardingTryOnModal({ visible, onClose, onTryNow }) {
  const { lang } = useI18n();
  const isFR = !lang || lang.startsWith('fr');

  const scaleAnim = useRef(new Animated.Value(0.7)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;
  const giftBounce = useRef(new Animated.Value(0)).current;
  const sparkleAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!visible) return;
    Animated.parallel([
      Animated.spring(scaleAnim, { toValue: 1, tension: 50, friction: 7, useNativeDriver: true }),
      Animated.timing(opacityAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
    ]).start();
    const bounceLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(giftBounce, { toValue: -8, duration: 800, useNativeDriver: true, easing: Easing.inOut(Easing.ease) }),
        Animated.timing(giftBounce, { toValue: 0, duration: 800, useNativeDriver: true, easing: Easing.inOut(Easing.ease) }),
      ])
    );
    const sparkleLoop = Animated.loop(
      Animated.timing(sparkleAnim, { toValue: 1, duration: 3000, useNativeDriver: true, easing: Easing.linear })
    );
    bounceLoop.start();
    sparkleLoop.start();
    return () => {
      bounceLoop.stop();
      sparkleLoop.stop();
    };
  }, [visible]);

  if (!visible) return null;

  const sparkleRotate = sparkleAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] });

  return (
    <AppModal visible transparent animationType="fade" onRequestClose={onClose} webBackdropClosable={false}>
      <View style={s.overlay}>
        <Animated.View style={[s.card, { transform: [{ scale: scaleAnim }], opacity: opacityAnim }]}>
          <View style={s.ribbonWrap}>
            <View style={s.ribbon}>
              <Text style={s.ribbonText}>🎁 {isFR ? 'CADEAU DE BIENVENUE' : 'WELCOME GIFT'}</Text>
            </View>
          </View>

          <View style={s.giftContainer}>
            <Animated.View style={{ transform: [{ rotate: sparkleRotate }], position: 'absolute' }}>
              <Text style={s.sparklesBg}>✨</Text>
            </Animated.View>
            <Animated.Text style={[s.giftEmoji, { transform: [{ translateY: giftBounce }] }]}>🪞</Animated.Text>
          </View>

          <Text style={s.title}>
            {isFR ? '1 Try-On offert' : '1 Free Try-On'}
          </Text>
          <Text style={s.subtitle}>
            {isFR
              ? 'Découvrez le Virtual Try-On Mytenue, sans engagement.'
              : 'Discover Mytenue Virtual Try-On, no commitment.'}
          </Text>

          <View style={s.explainCard}>
            <Text style={s.explainTitle}>
              {isFR ? '🪞 Qu\'est-ce qu\'un Try-On ?' : '🪞 What is a Try-On?'}
            </Text>
            <Text style={s.explainBody}>
              {isFR
                ? 'Notre IA habille un mannequin (ou votre propre photo) avec une tenue complète : un haut, un bas, des chaussures, un sac… Le tout combiné en une seule image réaliste.'
                : 'Our AI dresses a mannequin (or your own photo) with a complete outfit: top, bottom, shoes, bag… All combined into one realistic image.'}
            </Text>
            {/* v1.12.605 — Instruction pour utiliser l'offre */}
            <View style={s.howToBox}>
              <Text style={s.howToText}>
                {isFR
                  ? '👉 Cliquez sur le bouton '
                  : '👉 Tap the '}
                <Text style={s.howToButtonLabel}>
                  {isFR ? 'Essai' : 'Try-on'}
                </Text>
                {isFR
                  ? ' sur n\'importe quelle tenue pour profiter de l\'offre.'
                  : ' button on any outfit to use the offer.'}
              </Text>
            </View>
          </View>

          {/* v1.12.940 — Apple 1.1 : avis 18+ pour le Try-On sur photo perso */}
          <Text style={s.ageNote}>
            {isFR
              ? '🔞 Le Try-On sur votre propre photo est réservé aux 18 ans et plus. Utilisez uniquement votre propre photo.'
              : '🔞 Personal Try-On (on your own photo) is reserved for users aged 18 and over. Use your own photo only.'}
          </Text>

          <View style={s.benefitsRow}>
            <View style={s.benefit}>
              <Text style={s.benefitEmoji}>👤</Text>
              <Text style={s.benefitLabel}>
                {isFR ? 'Sur vous' : 'On you'}
              </Text>
            </View>
            <View style={s.benefit}>
              <Text style={s.benefitEmoji}>👔</Text>
              <Text style={s.benefitLabel}>
                {isFR ? 'Votre dressing' : 'Your wardrobe'}
              </Text>
            </View>
            <View style={s.benefit}>
              <Text style={s.benefitEmoji}>🛍️</Text>
              <Text style={s.benefitLabel}>
                {isFR ? 'Ou catalogue' : 'Or catalog'}
              </Text>
            </View>
          </View>

          <TouchableOpacity
            style={s.ctaPrimary}
            onPress={onTryNow}
            activeOpacity={0.85}
          >
            <Text style={s.ctaPrimaryText}>
              {isFR ? '✨ Essayer maintenant' : '✨ Try now'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onClose} style={s.ctaSecondary} activeOpacity={0.7}>
            <Text style={s.ctaSecondaryText}>
              {isFR ? 'Plus tard (valable 30 jours)' : 'Later (valid 30 days)'}
            </Text>
          </TouchableOpacity>
        </Animated.View>
      </View>
    </AppModal>
  );
}

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'transparent', // v1.12.605 — pas de background gris derrière le popup
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: COLORS.cream,
    borderRadius: 20,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
    shadowColor: COLORS.navy,
    shadowOpacity: 0.3,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 10 },
    elevation: 12,
    overflow: 'visible',
  },
  ribbonWrap: {
    position: 'absolute',
    top: -16,
    alignSelf: 'center',
  },
  ribbon: {
    backgroundColor: COLORS.gold,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 16,
    shadowColor: COLORS.gold,
    shadowOpacity: 0.4,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
  },
  ribbonText: {
    color: COLORS.navy,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.2,
  },
  giftContainer: {
    width: 140, height: 140,
    alignItems: 'center', justifyContent: 'center',
    marginTop: 20, marginBottom: 8,
  },
  giftEmoji: {
    fontSize: 76,
    textShadowColor: 'rgba(212, 175, 55, 0.5)',
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 10,
  },
  sparklesBg: {
    fontSize: 130,
    opacity: 0.15,
  },
  title: {
    fontSize: 28,
    fontWeight: '800',
    color: COLORS.navy,
    textAlign: 'center',
    marginBottom: 6,
    letterSpacing: -0.5,
  },
  subtitle: {
    fontSize: 14,
    color: COLORS.textLight,
    textAlign: 'center',
    marginBottom: 18,
    lineHeight: 20,
  },
  explainCard: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 14,
    width: '100%',
    marginBottom: 14,
    borderWidth: 1,
    borderColor: 'rgba(212, 175, 55, 0.3)',
  },
  explainTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: COLORS.navy,
    marginBottom: 6,
  },
  explainBody: {
    fontSize: 12,
    color: COLORS.text,
    lineHeight: 18,
  },
  howToBox: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: 'rgba(212, 175, 55, 0.25)',
  },
  howToText: {
    fontSize: 12,
    color: COLORS.text,
    lineHeight: 18,
  },
  howToButtonLabel: {
    fontWeight: '800',
    color: COLORS.navy,
    backgroundColor: 'rgba(212, 175, 55, 0.25)',
    paddingHorizontal: 4,
  },
  ageNote: {
    fontSize: 11,
    color: '#8A5A00',
    backgroundColor: '#FFF4E5',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    lineHeight: 16,
    textAlign: 'center',
    marginBottom: 14,
    width: '100%',
  },
  benefitsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    marginBottom: 18,
    paddingHorizontal: 4,
  },
  benefit: {
    alignItems: 'center',
    flex: 1,
  },
  benefitEmoji: {
    fontSize: 28,
    marginBottom: 4,
  },
  benefitLabel: {
    fontSize: 11,
    color: COLORS.textLight,
    fontWeight: '600',
    textAlign: 'center',
  },
  ctaPrimary: {
    backgroundColor: COLORS.navy,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 24,
    alignItems: 'center',
    width: '100%',
    marginBottom: 8,
    shadowColor: COLORS.gold,
    shadowOpacity: 0.3,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },
  ctaPrimaryText: {
    color: COLORS.gold,
    fontSize: 15,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  ctaSecondary: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  ctaSecondaryText: {
    color: COLORS.textLight,
    fontSize: 12,
    fontWeight: '600',
  },
});
